import { useEffect, useMemo, useState } from 'react';
import { adminApi, downloadBlob } from '@/lib/api';
import { Loader2, CalendarDays, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

export default function RawStockAuditPage() {
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [audit, setAudit] = useState(null);

  const rows = useMemo(() => audit?.daywise_rows || [], [audit]);
  const balances = useMemo(() => audit?.item_balances || [], [audit]);

  const downloadExcel = async (dataset) => {
    try {
      const res = await adminApi.downloadAdminDatasetExcel(dataset, {
        warehouse_id: selectedWarehouseId || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      downloadBlob(res.data, `${dataset}.xlsx`);
    } catch {
      toast.error('Failed to download Excel');
    }
  };

  const fetchWarehouses = async () => {
    const response = await adminApi.getWarehouses();
    const all = response.data || [];
    const filtered = all.filter((w) => {
      const name = (w.name || '').toLowerCase();
      return name.includes('packing') || w.tag === 'PACKING';
    });
    setWarehouses(filtered);

    if (filtered.length > 0) {
      const defaultPacking = filtered.find((w) => w.tag === 'PACKING') || filtered[0];
      setSelectedWarehouseId(defaultPacking.id);
      return defaultPacking.id;
    }
    return '';
  };

  const fetchAudit = async (warehouseId = selectedWarehouseId) => {
    if (!warehouseId) return;
    setLoading(true);
    try {
      const params = { warehouse_id: warehouseId };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const response = await adminApi.getRawStockDaywiseAudit(params);
      setAudit(response.data || null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to fetch raw stock audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const warehouseId = await fetchWarehouses();
        if (warehouseId) {
          await fetchAudit(warehouseId);
        } else {
          setAudit(null);
        }
      } catch (error) {
        toast.error('Failed to load warehouses');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  return (
    <div className="space-y-6" data-testid="raw-stock-audit-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#2D2420]">Raw Stock Audit</h1>
          <p className="text-[#A8A8A8] mt-1">Day-wise movement flow and current balance for Packing warehouse</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadExcel('raw-stock-audit-balances')}>
            <Download className="w-4 h-4 mr-2" /> Balance Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExcel('raw-stock-audit-daywise')}>
            <Download className="w-4 h-4 mr-2" /> Ledger Excel
          </Button>
        </div>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Warehouse</Label>
              <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select warehouse" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((warehouse) => (
                    <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>From Date</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="border-[#E5E5E5]" />
            </div>
            <div>
              <Label>To Date</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="border-[#E5E5E5]" />
            </div>
            <div className="flex items-end">
              <Button
                className="w-full bg-[#C97A5E] hover:bg-[#B86A4E]"
                onClick={() => fetchAudit()}
                disabled={loading || !selectedWarehouseId}
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Apply
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-7 h-7 animate-spin text-[#C97A5E]" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Movements Scanned</p><p className="text-2xl font-bold text-[#2D2420]">{audit?.summary?.movements_scanned || 0}</p></CardContent></Card>
            <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Day-wise Rows</p><p className="text-2xl font-bold text-[#2D2420]">{audit?.summary?.daywise_rows || 0}</p></CardContent></Card>
            <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Raw Items in Stock</p><p className="text-2xl font-bold text-[#2D2420]">{audit?.summary?.raw_items_with_balance || 0}</p></CardContent></Card>
            <Card className="bg-white border-[#E5E5E5]"><CardContent className="p-4"><p className="text-xs text-[#A8A8A8]">Negative Flow Events</p><p className="text-2xl font-bold text-[#E76F51]">{audit?.summary?.negative_clamp_events || 0}</p></CardContent></Card>
          </div>

          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg text-[#2D2420]">Current Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {balances.length === 0 ? (
                <div className="text-sm text-[#A8A8A8]">No stock balance found for selected filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Bags</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">KG</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">KG/Bag</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {balances.map((row) => (
                        <tr key={row.raw_item_id} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-3 px-4 text-[#2D2420]">{row.raw_item_name}</td>
                          <td className="py-3 px-4 text-right text-[#2D2420]">{Number(row.bags_total || 0).toFixed(3)}</td>
                          <td className="py-3 px-4 text-right text-[#2D2420]">{Number(row.kg_total || 0).toFixed(3)}</td>
                          <td className="py-3 px-4 text-right text-[#A8A8A8]">{Number(row.kg_per_bag || 0).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-white border-[#E5E5E5]">
            <CardHeader>
              <CardTitle className="text-lg text-[#2D2420]">Day-wise Movement Ledger</CardTitle>
            </CardHeader>
            <CardContent>
              {rows.length === 0 ? (
                <div className="text-sm text-[#A8A8A8]">No movement rows found for selected filters.</div>
              ) : (
                <div className="overflow-x-auto max-h-[520px]">
                  <table className="w-full">
                    <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5] sticky top-0 z-10">
                      <tr>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Raw Item</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Open KG</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">In KG</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Out KG</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Close KG</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E5E5]">
                      {rows.map((row, idx) => (
                        <tr key={`${row.date}-${row.raw_item_id}-${idx}`} className="hover:bg-[#FAFAF9]/60">
                          <td className="py-2 px-4 text-[#2D2420]">{row.date}</td>
                          <td className="py-2 px-4 text-[#2D2420]">{row.raw_item_name}</td>
                          <td className="py-2 px-4 text-right text-[#A8A8A8]">{Number(row.opening_kg || 0).toFixed(3)}</td>
                          <td className="py-2 px-4 text-right text-green-700">{Number(row.in_kg || 0).toFixed(3)}</td>
                          <td className="py-2 px-4 text-right text-[#E76F51]">{Number(row.out_kg || 0).toFixed(3)}</td>
                          <td className="py-2 px-4 text-right text-[#2D2420] font-medium">{Number(row.closing_kg || 0).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
